/**
 * 429 (όριο προσπαθειών): πόσα δευτερόλεπτα ζητά ο server να περιμένουμε.
 *
 * Το express-rate-limit του backend (pinLimiter / authLimiter, standardHeaders) στέλνει στο 429
 * `Retry-After: <δευτερόλεπτα>` και `RateLimit-Reset: <δευτερόλεπτα>`· το σώμα είναι μόνο
 * `{ error: 'Too many PIN attempts…' }`. Σειρά: Retry-After (δευτερόλεπτα ή ημερομηνία HTTP),
 * RateLimit-Reset, και τέλος retry_after / retryAfter στο σώμα.
 *
 * null = άγνωστο (π.χ. API σε άλλο origin χωρίς Access-Control-Expose-Headers: οι κεφαλίδες δεν φαίνονται).
 */
export const RETRY_AFTER_MAX_SECONDS = 3600;

type HeaderSource = { get(name: string): string | null } | null | undefined;

const toSeconds = (raw: unknown, now: number): number | null => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text) return null;
  if (/^\d+(\.\d+)?$/.test(text)) {
    const value = Number(text);
    // Μερικοί servers στέλνουν στιγμή (epoch δευτερόλεπτα) αντί για διάρκεια.
    return value > 1e9 ? Math.ceil(value - now / 1000) : Math.ceil(value);
  }
  const date = Date.parse(text);
  return Number.isNaN(date) ? null : Math.ceil((date - now) / 1000);
};

export function retryAfterSeconds(headers: HeaderSource, body?: unknown, now: number = Date.now()): number | null {
  const header = (name: string): string | null => {
    try { return headers?.get?.(name) ?? null; } catch { return null; }
  };
  const fields = body && typeof body === 'object' ? body as Record<string, unknown> : {};
  for (const raw of [header('Retry-After'), header('RateLimit-Reset'), fields.retry_after, fields.retryAfter]) {
    const seconds = toSeconds(raw, now);
    if (seconds !== null && Number.isFinite(seconds)) return Math.min(Math.max(seconds, 1), RETRY_AFTER_MAX_SECONDS);
  }
  return null;
}
